import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Button,
  ButtonVariant,
  DropdownMenu,
  Icon,
  IconName,
  Modal,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';
import { PermissionChecker } from '~/helpers/components/PermissionChecker';
import { openZendeskSupportForm } from '~/helpers/zendesk';
import {
  StyledIcon,
  ExternalLinkIcon,
  HelpMenuItemContent,
  HelpMenuTitle,
  HelpMenuDescription,
} from '~/dashboard/components/Sidebar/SidebarStyle';
import { snakeToCaps } from '~/helpers/formatting';
import {
  ONGOING_ALERTS_REBRAND,
  PRODUCT_UPDATES_API_ENDPOINT, // Move to environment variables!
  PRODUCT_UPDATES_API_HEADERS, // Move to environment variables!
  PRODUCT_UPDATES_URL,
  RULE_LIBRARY_ORGANIZATION_SLUG,
} from '~/constants';
import { getIsParentAgentAccessingChildCustomer } from '~/dashboard/helpers';
import { AdminSidebarLinks } from '~/dashboard/components/Sidebar/AdminSidebarLinks';

import {
  ProductUpdates,
  useProductUpdatesData,
} from './ProductUpdates';

const CustomerSidebarLinks = () => {
  const {
    agent: {
      isEmployee,
    },
    agent,
    customer,
    featureFlags,
    permissions,
    reviewQueueEnabled,
  } = useContext(UserContext);

  const isParentAgentAccessingChildCustomer = getIsParentAgentAccessingChildCustomer({
    agent,
    customer,
  });

  const location = useLocation();
  const getEntityEvaluationLinkClass = path => {
    if (!path && !location?.pathname?.length) return '';
    const { pathname } = location;
    const evalPath = path.indexOf('/evaluations') > -1;
    const evalPathname = pathname.indexOf('/evaluations') > -1;
    if (
      evalPath && evalPathname
    ) return 'active';
    const entityPath = path.indexOf('/entities') > -1;
    const entityPathname = pathname.indexOf('/entities') > -1;
    if (
      entityPath && entityPathname && !evalPathname
    ) return 'active';
    return '';
  };
  const getNavLinkClass = path => (
    (path && location.pathname && location.pathname.slice(0, path.length)) === path
      ? 'active' : '');
  const getWorkflowsNavLinkClass = path => (
    (path && location.pathname && (location.pathname.includes('workflows') || location.pathname.includes('snippets')))
      ? 'active' : ''
  );
  const getJourneyNavLinkClass = path => (
    (!location.pathname.includes('applications') && !location.pathname.includes('alerts'))
      ? getNavLinkClass(path)
      : ''
  );
  const getApplicationReviewNavLinkClass = path => (
    location.pathname.includes('applications')
      ? getNavLinkClass(path) : '');
  const getAlertReviewNavLinkClass = path => (
    location.pathname.includes('alerts')
      ? getNavLinkClass(path) : '');
  const getInvestigationsNavLinkClass = path => (
    location.pathname.includes('investigations')
      ? getNavLinkClass(path) : '');
  const showJourneysLink = featureFlags.journeys && permissions['READ:Journey:journey_id'];
  const showAppQueueLink = permissions['READ:JourneyApplications:null'];
  const showAlertsLink = featureFlags?.standaloneAlertsPage;

  const shouldShowReviewQueue = featureFlags.journeys ? !featureFlags.hideReviewQueue : true;
  const showInvestigations = featureFlags.investigations
    && permissions['READ:Investigations:null'];

  const shouldShowSarReports = (
    featureFlags.sarReport
    && (
      featureFlags.reportingPermissions
        ? permissions['READ:SarReports:sar_report_id']
        : permissions['READ:SarReports:null']
    )
  );

  const shouldShowCurrencyTransactionReports = (
    featureFlags.currencyTransactionReports
    && permissions['READ:CtrReports:null']
  );

  const shouldShowPartnerCenter = (
    featureFlags.enablePartnerCenterV2
    && permissions['READ:Services:null']
  );

  const isRuleLibraryAccount = customer?.organizationNameSlug === RULE_LIBRARY_ORGANIZATION_SLUG;

  return (
    <>
      <li className={`${getJourneyNavLinkClass(routes.v3.dashboard.home.dashboard)} flex center`} data-dd-action-name="alloydd-sidebar-nav-home">
        <span className="active-bar" />
        <a href={`${window.location.origin}${routes.v3.dashboard.home.dashboard}`} className="flex">
          <StyledIcon icon={IconName.Home} />
          <span className="text fs-unmask">Home</span>
        </a>
      </li>

      {/* Rule-library accounts only see Home and Settings */}
      {isRuleLibraryAccount ? (
        <>
          {/* Parent agents viewing child customers should not be able to see the settings tab. */}
          {!isParentAgentAccessingChildCustomer && (
            <PermissionChecker
              permissions={featureFlags.afefAuditPermissions
                ? ['READ:Settings:null']
                : []}
            >
              <li className={`${getNavLinkClass(routes.v3.dashboard.settings.index)} flex center`} data-dd-action-name="alloydd-sidebar-nav-settings">
                <span className="active-bar" />
                <a href={`${window.location.origin}${routes.v3.dashboard.settings.index}`} className="flex">
                  <StyledIcon icon={IconName.Settings} />
                  <span className="text fs-unmask">Settings</span>
                </a>
              </li>
            </PermissionChecker>
          )}
        </>
      ) : (
        <>
          {/* Regular customers see all navigation items */}
          {permissions['READ:Entities:null'] && (
            <li className={`${getEntityEvaluationLinkClass(routes.v3.dashboard.evaluations.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-evaluations">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.evaluations.list}`} className="flex">
                <StyledIcon icon={IconName.Evaluations} />
                <span className="text fs-unmask">Evaluations</span>
              </a>
            </li>
          )}

          {permissions['READ:Entities:null'] && (
            <li className={`${getEntityEvaluationLinkClass(routes.entities.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-entities">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.entities.list}`} className="flex">
                <StyledIcon icon={IconName.Entities} />
                <span className="text fs-unmask">Entities</span>
              </a>
            </li>
          )}

          {permissions['READ:Entities:null'] && reviewQueueEnabled && shouldShowReviewQueue && (
            <li className={`${getNavLinkClass(routes.v3.dashboard.queues.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-review-queue">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.queues.list}`} className="flex">
                <StyledIcon icon={IconName.ReviewQueue} />
                <span className="text fs-unmask">Review Queue</span>
              </a>
            </li>
          )}

          {showJourneysLink && showAppQueueLink && (
            <li className={`${getApplicationReviewNavLinkClass(routes.v3.dashboard.journeys.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-application-queue">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.journeys.applications.list}`} className="flex">
                <StyledIcon icon={IconName.ApplicationReview} />
                <span className="text fs-unmask">{featureFlags?.alertGenerationInJourneys ? `${snakeToCaps(ONGOING_ALERTS_REBRAND.APPLICATION)} Queue` : 'Application Queue'}</span>
              </a>
            </li>
          )}

          {showAlertsLink && showJourneysLink && showAppQueueLink && (
            <li className={`${getAlertReviewNavLinkClass(routes.v3.dashboard.journeys.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-alerts">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.journeys.alerts.list}`} className="flex">
                <StyledIcon icon={IconName.BoltAlert} />
                <span className="text fs-unmask">{featureFlags.investigations ? 'Alerts' : 'Alert Queue' }</span>
              </a>
            </li>
          )}

          {showInvestigations && (
            <li className={`${getInvestigationsNavLinkClass(routes.investigations.queue)} flex center`} data-dd-action-name="alloydd-sidebar-nav-investigations">
              <span className="active-bar" />
              <Link to={routes.investigations.queue} className="flex">
                <StyledIcon icon={IconName.FolderOpenInvestigation} />
                <span className="text fs-unmask">Investigations</span>
              </Link>
            </li>
          )}

          {permissions['READ:Entities:null'] && featureFlags.caseManagement && (
            <li className={`${getNavLinkClass(routes.v3.dashboard.cases.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-case-management">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.cases.list}`} className="flex">
                <StyledIcon icon={IconName.CaseManagement} />
                <span className="text fs-unmask">Case Management</span>
              </a>
            </li>
          )}
          {shouldShowSarReports && !isEmployee && (
            <li className={`${getNavLinkClass(routes.sars.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-sars">
              <span className="active-bar" />
              <Link to={routes.sars.list} className="flex">
                <StyledIcon icon={IconName.DocumentWarning} />
                <span className="text fs-unmask">SARs</span>
              </Link>
            </li>
          )}

          {shouldShowCurrencyTransactionReports && (
            <li className={`${getNavLinkClass(routes.v3.dashboard.ctrs.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-ctrs">
              <span className="active-bar" />
              <Link to={routes.v3.dashboard.ctrs.list} className="flex">
                <StyledIcon icon={IconName.FileInvoiceDollar} />
                <span className="text fs-unmask">CTRs</span>
              </Link>
            </li>
          )}

          {(
            permissions['READ:Applications:application_id']
            || permissions['READ:Journey:journey_id']
          ) && (
            <li className={`${getWorkflowsNavLinkClass(routes.v3.dashboard.workflows.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-workflows">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.workflows.list}`} className="flex">
                <StyledIcon icon={IconName.Workflows} />
                <span className="text fs-unmask">Workflows</span>
              </a>
            </li>
          )}

          {showJourneysLink && (
            <li className={`${getJourneyNavLinkClass(routes.v3.dashboard.journeys.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-journeys">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.journeys.list}`} className="flex">
                <StyledIcon icon={IconName.Journey} />
                <span className="text fs-unmask">Journeys</span>
              </a>
            </li>
          )}

          {shouldShowPartnerCenter && (
            <li className={`${getNavLinkClass(routes.v3.dashboard.partnerSolutions.list)} flex center`} data-dd-action-name="alloydd-sidebar-nav-partner-solutions">
              <span className="active-bar" />
              <a href={`${window.location.origin}${routes.v3.dashboard.partnerSolutions.list}`} className="flex">
                <StyledIcon icon={IconName.PartnerSolutions} />
                <span className="text fs-unmask">Partner Center</span>
              </a>
            </li>
          )}

          {/* Parent agents viewing child customers should not be able to see the settings tab. */}
          {!isParentAgentAccessingChildCustomer && (
            <PermissionChecker
              permissions={featureFlags.afefAuditPermissions
                ? ['READ:Settings:null']
                : []}
            >
              <li className={`${getNavLinkClass(routes.v3.dashboard.settings.index)} flex center`} data-dd-action-name="alloydd-sidebar-nav-settings">
                <span className="active-bar" />
                <a href={`${window.location.origin}${routes.v3.dashboard.settings.index}`} className="flex">
                  <StyledIcon icon={IconName.Settings} />
                  <span className="text fs-unmask">Settings</span>
                </a>
              </li>
            </PermissionChecker>
          )}
        </>
      )}
    </>
  );
};


const ProductUpdatesModal = ({ isOpen, onClose }) => {
  const productUpdatesData = useProductUpdatesData({
    endpoint: PRODUCT_UPDATES_API_ENDPOINT,
    headers: PRODUCT_UPDATES_API_HEADERS,
    limit: 10,
    enabled: isOpen,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Product Updates"
      size="medium"
      hasCloseIcon
      primaryAction={(
        <Button
          variant={ButtonVariant.primary}
          onClick={onClose}
        >
          Close
        </Button>
      )}
      tertiaryAction={(
        <Button
          variant={ButtonVariant.tertiary}
          onClick={() => {
            onClose();
            window.open(PRODUCT_UPDATES_URL, '_blank');
          }}
        >
          View Entire Update Collection
        </Button>
      )}
    >
      <ProductUpdates productUpdatesData={productUpdatesData} />
    </Modal>
  );
};
ProductUpdatesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const SidebarMenuResourcesButtonPropTypes = {
  sidebarExpanded: PropTypes.bool.isRequired,
};

const SidebarMenuResourcesButton = ({ sidebarExpanded }) => {
  const { agent } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [showProductUpdates, setShowProductUpdates] = useState(false);

  return (
    <>
      <li
        className="flex center"
        data-dd-action-name="alloydd-sidebar-nav-resources"
        style={{
          marginBottom: 'var(--cl-space-4)', // to shift the button up from the bottom of the page a bit
        }}
      >
        <DropdownMenu
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          align="center"
          side="right"
          style={{
            padding: '4px',
            marginBottom: 'var(--cl-space-4)', // to shift the menu up from the bottom of the page a bit
          }}
          trigger={(
            <a tabIndex={0} role="button" title="Resources">
              <StyledIcon icon={IconName.Question} />
              <span className="text fs-unmask">Resources</span>
            </a>
          )}
        >
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-resources-menu-option-product-updates"
            onSelect={() => {
              setIsOpen(false);
              setShowProductUpdates(true);
            }}
          >
            <Icon icon={IconName.Gift} />
            <HelpMenuItemContent>
              <HelpMenuTitle>Product Updates</HelpMenuTitle>
              <HelpMenuDescription>Get the latest product news</HelpMenuDescription>
            </HelpMenuItemContent>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-resources-menu-option-alloy-lab"
            onSelect={() => {
              setIsOpen(false);
              window.open('https://alloy.docebosaas.com/learn', '_blank');
            }}
          >
            <Icon icon={IconName.LifeRing} />
            <HelpMenuItemContent>
              <HelpMenuTitle>Learn Alloy Better</HelpMenuTitle>
              <HelpMenuDescription>
                Our platform training portal
              </HelpMenuDescription>
            </HelpMenuItemContent>
            <ExternalLinkIcon icon={IconName.ArrowUpRightFromSquare} />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            data-dd-action-name="alloydd-sidebar-nav-resources-menu-option-support"
            onSelect={() => {
              setIsOpen(false);
              openZendeskSupportForm(sidebarExpanded, agent);
            }}
          >
            <Icon icon={IconName.Message} />
            <HelpMenuItemContent>
              <HelpMenuTitle>Support</HelpMenuTitle>
              <HelpMenuDescription>View our docs and get in touch</HelpMenuDescription>
            </HelpMenuItemContent>
          </DropdownMenu.Item>
        </DropdownMenu>
      </li>

      <ProductUpdatesModal
        isOpen={showProductUpdates}
        onClose={() => setShowProductUpdates(false)}
      />
    </>
  );
};

const SidebarPropTypes = {
  expanded: PropTypes.bool.isRequired,
  toggleExpanded: PropTypes.func.isRequired,
};

const Sidebar = ({ expanded, toggleExpanded }) => {
  const location = useLocation();
  const inAdminDashboard = location.pathname.startsWith('/v3/dashboard/admin');

  return (
    <nav id="sidebar" className={expanded ? 'expanded' : ''}>
      <ul className="list-unstyled components">
        <li className="flex center expanded-toggle">
          <a onClick={toggleExpanded} />
        </li>

        {inAdminDashboard ? <AdminSidebarLinks /> : <CustomerSidebarLinks />}
      </ul>
      <ul className="list-unstyled components">
        {!inAdminDashboard && <SidebarMenuResourcesButton sidebarExpanded={expanded} />}
      </ul>
    </nav>
  );
};

Sidebar.propTypes = SidebarPropTypes;
SidebarMenuResourcesButton.propTypes = SidebarMenuResourcesButtonPropTypes;

export default Sidebar;
